import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { AccountingHeading } from 'src/features/accountingHeaders/utils/types';
import {
    accountingHeadingFormSchema,
    AccountingHeadingFormSchemaValues,
} from 'src/features/accountingHeaders/utils/formSchema';
import { useGetAccountingHeading } from 'src/features/accountingHeaders/api/useGetAccountingHeading';
import AccountingHeaderForm from './AccountingHeaderForm';
import { useListAccountingHeadings } from 'src/features/accountingHeaders/api/useListAccountingHeadings';

interface AccountingHeaderFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAccountingHeader?: AccountingHeading;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: AccountingHeadingFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function AccountingHeaderFormModal<TData>({
    selectedAccountingHeader,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: AccountingHeaderFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [searchAccountingHeadings, setSearchAccountingHeadings] = React.useState<string>('');

    const { data: accountingHeaders } = useListAccountingHeadings({
        search: searchAccountingHeadings,
    });

    const { data: accountingHeader } = useGetAccountingHeading({
        id: selectedAccountingHeader?.id ?? '',
        config: { enabled: !!selectedAccountingHeader?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            name: accountingHeader?.name ?? '',
            account: accountingHeader?.account ?? '',
            type: accountingHeader?.type ?? undefined,
            partide: accountingHeader?.partide ?? undefined,
            billing_entity: accountingHeader?.billing_entity.id ?? '',
            parent: accountingHeader?.parent?.id ?? undefined,
        };
    }, [accountingHeader]);

    const form = useForm<AccountingHeadingFormSchemaValues>({
        resolver: zodResolver(accountingHeadingFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [accountingHeader, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: AccountingHeadingFormSchemaValues) =>
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            ),
        [mutate, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <AccountingHeaderForm
                        form={form}
                        onSubmit={onSubmit}
                        accountingHeaders={accountingHeaders?.results}
                        setSearchAccountingHeadings={setSearchAccountingHeadings}
                    />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedAccountingHeader && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default AccountingHeaderFormModal;
